<script setup lang="ts">
import BackstageVideo from '@/components/common/BackstageVideo.vue';
import { richTextFromMarkdown } from '@contentful/rich-text-from-markdown';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { onMounted, ref } from 'vue';
import BackstageImage from './BackstageImage.vue';

export interface Props {
  description: string;
  browserNotSupportedVideoText: string;
  playVideoAriaLabel: string;
  title?: string;
  titleUrl?: string;
  imageUrl?: string;
  video?: {
    url: string;
    thumbnail: string;
  };
}

const props = defineProps<Props>();

const renderedDescription = ref('');

onMounted(async () => {
  const doc = await richTextFromMarkdown(props.description);
  renderedDescription.value = documentToHtmlString(doc);
  renderedDescription.value = renderedDescription.value.replace('<a', '<a target="_blank" rel="noopener noreferrer" ');
});
</script>

<template>
  <div class="tutorial-guidance-step-content">
    <header v-if="title" id="driver-popover-title">
      <a v-if="titleUrl" class="driver-popover-title link" :href="titleUrl" target="_blank" rel="noopener noreferrer">
        {{ title }}
      </a>
      <p v-else class="driver-popover-title">{{ title }}</p>
    </header>
    <div class="scrollable-content">
      <BackstageImage class="driver-popover-image" v-if="imageUrl" :src="imageUrl" :alt="description" />
      <BackstageVideo
        v-if="video"
        :src="video.url"
        :poster="video.thumbnail"
        :fallback-text="browserNotSupportedVideoText"
        :play-aria-label="playVideoAriaLabel"
        class="driver-popover-video-container" />
      <p class="tutorial-description" v-html="renderedDescription"></p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tutorial-guidance-step-content {
  display: flex;
  flex-direction: column;
  max-height: min(calc(90vh - 140px), 600px);

  header {
    flex-shrink: 0;
    text-align: left;
    margin-bottom: 16px;
  }

  .scrollable-content {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .driver-popover-image {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 6px;
    object-fit: contain;
  }

  .driver-popover-title {
    font-size: 26px !important;
    display: inline-block;

    &.link {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .tutorial-description {
    text-align: left;
    font-size: 20px !important;
    font-weight: 400;

    &:deep() {
      a {
        text-decoration: underline !important;
      }
    }
  }
}
</style>
